import { DeclarationHighValueGroupType } from '@e-origin/shared';

export const AUTH_COOKIE_NAME = (import.meta as any).env.VITE_AUTH_COOKIE_NAME ?? 'eorigin-declarations-token';
export const ADMIN_AUTH_COOKIE_NAME = (import.meta as any).env.VITE_ADMIN_AUTH_COOKIE_NAME ?? 'eorigin-admin-token';
export const RECAPTCHA_SITE_KEY = (import.meta as any).env.VITE_RECAPTCHA_SITE_KEY ?? '6LfadEwsAAAAAKTgNOrUl6lsreF4e8xgmUdHYlFW';
export const API_ENCODING_KEY = (import.meta as any).env.VITE_API_ENCODING_KEY ?? 'WAfCjs2IfGiwqEMof6jP0O1Vv3bNdqpq';
export const AUTH_EMAIL_NAME = 'user-email';

const ADMIN_PATH_ENV = (import.meta as any).env.VITE_ADMIN_PATH as string | undefined;
export const isAdminBuild = ADMIN_PATH_ENV !== undefined;
export const getAdminBase = () => ADMIN_PATH_ENV ?? '/admin';

const ENV_PREFIXES = ['test', 'staging', 'production'];

// Admin labels are `<env>-<customer>` (e.g. staging-customs-trade), while the customer host is
// `<env>.<customer>` (staging.customs-trade). Only the first hyphen after an env prefix is a
// separator; later hyphens belong to the customer name.
const toCustomerLabel = (label: string) => {
  const [prefix, ...rest] = label.split('-');
  return ENV_PREFIXES.includes(prefix) && rest.length > 0 ? `${prefix}.${rest.join('-')}` : label;
};

export const getCustomerAppOrigin = () => {
  if (!isAdminBuild) return window.location.origin;
  const host = window.location.hostname
    .split('.')
    .filter((label) => label !== 'admin')
    .map(toCustomerLabel)
    .join('.');
  const port = window.location.port ? `:${window.location.port}` : '';
  return `${window.location.protocol}//${host}${port}`;
};
export const ADDRESS_COUNTRY_MAX_LENGTH = 2;

export const STALE_TIME_USE_DECLARANT_MIN = 10;
export const BATCH_LIST_HOURS = 3;
export const BATCH_LIST_NEXT_PAGE = 2;
export const BATCH_LIST_PERCENTAGE = 100;
export const BATCHES_ARCHIVE_MONTHS = 2;
export const BE_GATE_MAX_DAYS = 10;
export const BULK_UPDATE_BATCH_SIZE = 100;
export const VIEW_DETAILS = 'View Details';

export const CUSTOMS_SENDING_DELAY_SECONDS = 3;
export const CUSTOMS_SENDING_DELAY_MIN_SECONDS = 0.1;
export const DEFAULT_TRANSIT_PROCESS_ID_SEQUENCE_NUMBER = '00';

export const DATA_TABLE_PAGE_SIZE = 4;
export const DATA_TABLE_PAGE_SIZE_EXTRA_LARGE = 100;
export const DATA_TABLE_PAGE_SIZE_LARGE = 50;
export const DATA_TABLE_PAGE_SIZE_MEDIUM = 20;
export const DATA_TABLE_PAGE_SIZE_SMALL = 10;
export const DATEPICKER_WIDTH_PERCENTAGE = 100;
export const DEBOUNCE_TIME = 300;
export const DECIMAL_PRECISION_FACTOR = 10;
export const DECLARATION_HV_CODE_SLICE = 4;
export const DECLARATION_CUSTOMS_PROCEDURE_SUBSTRING_SEPARATOR = 2;
export const DECLARATION_CUSTOMS_PROCEDURE_LENGTH = 4;

export const DECLARATION_VIEW_PROOF_JSON_INDENT = 2;
export const DEFAULT_DECIMAL_PLACES = 2;
export const DROPDOWN_MAX_MENU_HEIGHT = 100;

export const EDIT_GOOD_LOCATION_SUBMIT_DELAY = 1000;
export const EURO_DECIMAL_PLACES = 2;
export const EVALUATION_CALCULATOR_PERCENTAGE_FACTOR = 100;
export const EVALUATION_CALCULATOR_WIDTH_PERCENTAGE = 100;
export const EVALUATION_IMADB_PERCENTAGE_FACTOR = 100;
export const EVALUATION_WEB_VALUE_PERCENTAGE_FACTOR = 100;

export const FONT_WEIGHT_REGULAR = 400;
export const FONT_WEIGHT_SEMIBOLD = 600;

export const GOODS_COUNTRY_CODE_MAX_LENGTH = 2;

export const HS_CODE_DESC_LIMIT = 2;
export const HS_CODE_MAGNITUDE_FACTOR = 15;
export const HS_CODE_TARIC_DESCRIPTION_LIMIT = 4;
export const HS_CODE_TARIC_SUBSTRING_LIMIT = 2;
export const HS_CODE_POSITION_INDEX = 3;

export const KG_DECIMAL_PLACES = 2;

export const LISTFILTER_RANDOM_ID_CHAR_COUNT = 7;
export const LISTFILTER_RANDOM_ID_CONVERSION_BASE = 36;

export const MANUALLY_SET_PRICE_MIN_VALUE = 0.01;
export const MILLISECONDS_IN_SECOND = 1000;
export const MULTILANG_WIDTH_PERCENTAGE = 100;
export const MULTISELECT_DEBOUNCE_TIME = 300;
export const MINUTES_IN_HOUR = 60;

export const NON_ARRIVAL_COUNTRY_CODE_LENGTH = 2;
export const NON_ARRIVAL_REFERENCE_NUMBER_LENGTH = 8;

export const PASSWORD_MIN_LENGTH = 8;
export const PERCENTAGE_FACTOR = 100;

export const REPRESENTATIVE_STATUS_DECLARANT = 1;
export const REPRESENTATIVE_STATUS_DIRECT = 2;
export const REPRESENTATIVE_STATUS_FIXED = 3;

export const RISK_DATE_ISO_SLICE_LIMIT = 10;
export const RISK_HS_CODE_CUSTOMS_SYSTEM_LENGTH = 6;
export const RISK_HS_CODE_PREFIX_LENGTH = 6;
export const RISK_HS_CODE_TARIC_CODE_LENGTH = 2;
export const RISK_INITIAL_WEB_DESC_LIMIT = 10;
export const RISK_PREDICTION_OCCURRENCE_INDEX = 2;
export const RISK_PREDICTION_PROBABILITY_INDEX = 3;
export const RISK_RATE_PERCENTAGE_FACTOR = 100;
export const RISK_VALIDATION_REQUIRED = 3;

export const SEARCH_DEBOUNCE_DELAY = 500;
export const SECONDS_IN_HOUR = 3600;
export const SECONDS_IN_MINUTE = 60;
export const SEND_BATCH_RANDOM_DELAY_MAX = 2000;
export const SENDING_INDICATOR_DOT_COUNT = 5;

export const TABLE_COLUMN_WIDTH = 100;
export const TABLE_ITEMS_PER_PAGE = 10;
export const TABLE_SELECTION_COLUMN_WIDTH = 40;
export const TABLE_HEADER_ICON_STROKE_SELECTED = 2.5;
export const TABLE_HEADER_ICON_STROKE_UNSELECTED = 1.5;
export const TABLE_MENU_THRESHOLD = 3;

export const TOKEN_EXPIRATION_BUFFER_SECONDS = 30;
export const TRANSIT_AUTOCOMPLETE_DEFAULT_WIDTH = 50;
export const TRANSIT_DUPLICATE_DEFAULT_INPUTS_PER_ROW = 3;
export const TRANSIT_DUPLICATE_INPUT_DEFAULT_WIDTH = 100;
export const TRANSIT_DUPLICATE_MAX_ROWS_DEFAULT = 9999;
export const TRANSIT_DUPLICATE_SEPARATOR_COLUMN_COUNT = 2;
export const TRANSIT_DUPLICATE_SEPARATOR_WIDTH_DEFAULT = 74;
export const TRANSIT_DUPLICATE_SEPARATOR_WIDTH_TWO_COLUMNS = 49;
export const TRANSIT_INPUT_HEADER_WIDTH = 50;
export const TRANSIT_SECURITY_GRID_COL_ARRIVAL = 3;
export const TRANSIT_SECURITY_GRID_COL_DEPARTURE = 2;

export const UPLOADFILE_THEME_COLOR_INDEX = 6;
export const USER_LAYOUT_PER_SECOND = 1000;

export const VALUATION_INDICATORS_LENGTH = 4;
export const VAT_REPORT_MONTHS_IN_YEAR = 12;
export const VAT_REPORT_QUARTERS_IN_YEAR = 4;
export const VAT_REPORT_YEAR_HISTORY_COUNT = 5;

export const WEBSOCKET_MAX_RECONNECT_DELAY_MS = 5000;
export const WEBSOCKET_NORMAL_CLOSURE_CODE = 1000;
export const WEBSOCKET_RECONNECT_BASE_FACTOR = 2;
export const WEBSOCKET_RECONNECT_MULTIPLIER_MS = 1000;
export const WEBSOCKET_CONNECTION_CHECK_INTERVAL = 180000;

export const USER_TOKEN_EXPIRATION_TIME = 1800000;
export const USER_TOKEN_REFRESH_BEFORE_EXPIRATION = 60000;

export const DAY_IN_MS = 24 * 60 * 60 * 1000;

export const ALLOWED_HV_REQUESTED_PROCEDURES = {
  [DeclarationHighValueGroupType.H1]: ['01', '07', '40', '42', '43', '44', '45', '46', '48', '61', '63', '68'],
  [DeclarationHighValueGroupType.H2]: ['71'],
  [DeclarationHighValueGroupType.H3]: ['53'],
  [DeclarationHighValueGroupType.H4]: ['51'],
  [DeclarationHighValueGroupType.H5]: ['40', '42', '61', '63', '68', '95', '96'],
  [DeclarationHighValueGroupType.H6]: ['01', '07', '40'],
};

export const NOT_AVAILABLE = 'N/A';
